import Repository from "./Repository";
import Employee from "../types/Employee";
import EmployeesResponse from "./response/EmployeesResponse";
import { EmployeeResponse } from "../types/EmployeeResponse";
import BaseResponse from "../types/BaseResponse";
import DateHelper from "../helpers/DateHelper";

const resource = "/employee";

export default {
  get() {
    return Repository.get(`${resource}`);
  },
  getByEmployeeId(employeeId: string) {
    return Repository.get(`${resource}/${employeeId}`);
  },
  getByCompany(
    companyId: string,
    sortBy: number,
    count: number,
    pageNum: number,
    orderDirection: string,
    search: string,
    showInactive: boolean,
    supervisorRole: boolean,
    groupManagerRole: boolean,
    clerical: boolean
  ) {
    let headers = {
      supervisorRole: supervisorRole,
      groupManagerRole: groupManagerRole,
      clerical: clerical,
    };
    return Repository.get(
      `${resource}/company/${companyId}?sortBy=${sortBy}&itemCount=${count}&pageNum=${pageNum}&search=${search}&sortOrder=${orderDirection}&showInActive=${showInactive}`,
      { headers }
    );
  },
  getAllByCompany(companyId: string) {
    return Repository.get(`${resource}/company/${companyId}`);
  },
  getAllByCompanyForExport(companyId: string) {
    return Repository.get(`${resource}/company/${companyId}/export`);
  },
  getOne(id: string) {
    return Repository.get(`${resource}/${id}`);
  },
  getByUserId(id: string, username: string, emailVerified?: boolean) {
    let encodedId = encodeURI(id);
    return Repository.get<BaseResponse<Employee>>(
      `${resource}/byUserId/${encodedId}?userName=${username}&emailVerified=${emailVerified}`
    );
  },
  create(employee: Employee) {
    return Repository.post(`${resource}`, employee);
  },
  addEmployees(employees: Employee[]) {
    return Repository.post<BaseResponse>(`${resource}/addEmployees`, employees);
  },
  save(employee: Employee) {
    if (employee.employeeId && employee.employeeId.length > 0) {
      return Repository.patch(`${resource}`, employee);
    } else {
      return Repository.post(`${resource}/addEmployee`, employee);
    }
  },
  updateEmployeesStatus(employeeIds: Array<string>, active: boolean) {
    return Repository.patch(
      `${resource}/updateEmployeeStatus/${active}`,
      employeeIds
    );
  },

  findDirectReports(employeeIds: Array<string>) {
    return Repository.patch(
      `${resource}/findDirectReports`,
      employeeIds
    );
  },

  reassignEmployees(employeeIds: Array<string>, currentSupervisorId: string,
    newSupervisorId: string) {
    return Repository.patch(
      `${resource}/reassign/from/${currentSupervisorId}/to/${newSupervisorId}`,
      employeeIds
    );
  },

  getTotalCount(companyId: string, showInactive: boolean) {
    return Repository.get(
      `${resource}/company/${companyId}/totalCount?showInActive=${showInactive}`
    );
  },
  searchByCompany(companyId: string, keyword: string) {
    return Repository.get<BaseResponse<Employee[]>>(
      `${resource}/search/company/${companyId}?keyword=${keyword}`
    );
  },
  getTeam(employeeId: string) {
    return Repository.get<EmployeesResponse>(`${resource}/${employeeId}/team`);
  },
  getManager(employeeId: string) {
    return Repository.get<EmployeeResponse>(
      `${resource}/${employeeId}/manager`
    );
  },
  changePassword(employeeId: string, password: string) {
    return Repository.post<BaseResponse>(
      `${resource}/${employeeId}/resetPassword`,
      {
        password,
      }
    );
  },
  updateExternalId(employeeId: string, externalId: string) {
    return Repository.patch<EmployeeResponse>(
      `${resource}/${employeeId}/updateExternalId/${externalId}`,
      {
        externalId,
      }
    );
  },
  updateTos(employeeId: string) {
    const tosDate = DateHelper.formatDate(new Date());
    return Repository.patch(`${resource}/${employeeId}/tosDate/${tosDate}`);
  },
  sendVerificationEmail(employeeId: string) {
    return Repository.get<BaseResponse>(
      `${resource}/${employeeId}/sendEmailVerification`
    );
  },
};
